import React, {useContext, useEffect, useState} from 'react'
import {
    Alert,
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import DatePicker from '@mui/lab/DatePicker'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {AppContext} from '../../contexts/AppContextProvider'
import {ProductTypeOptions, TreatmentDatesState} from '../../contexts/types'
import {isValidFn, useForm} from '../../hooks/useForm'
import {endOfDay, endOfToday, isAfter, isBefore, isDate, isSameDay, isValid, startOfDay,} from 'date-fns'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import {setTreatmentDates} from "../../contexts/TreatmentDatesReducer";
import UseTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";

const isValidDate: isValidFn<TreatmentDatesState> = (value, data) => {
    if (!value) return true
    if (
        isDate(value) &&
        isValid(value) &&
        isBefore(value as Date, endOfToday()) &&
        (isSameDay(value as Date, startOfDay(new Date(1986, 0, 1))) ||
            isAfter(value as Date, startOfDay(new Date(1986, 0, 1))))
    ) {
        return true
    }
    return false
}

const TreatmentDates: React.FC = () => {
    const {navigateForward} = UseTheHackiestPageNavigation()
    const {dateMask, complaint, dispatch, postNotification, clearNotification} = useContext(AppContext)
    const {t} = useContext(OrgCustomizationContext)
    const [dateOfImplantLoadingErrorMsg, setDateOfImplantLoadingErrorMsg] =
        useState<string>('')
    const [dateOfSecondSurgeryErrorMsg, setDateOfSecondSurgeryErrorMsg] =
        useState<string>('')
    const [dateOfProductRemovalErrorMsg, setDateOfProductRemovalErrorMsg] =
        useState<string>('')
    const [dateOfPlacementErrorMsg, setDateOfPlacementErrorMsg] =
        useState<string>('')
    const [
        dateOfProductRemovalToSecondarySurgeryErrorMsg,
        setDateOfProductRemovalToSecondarySurgeryErrorMsg
    ] =
        useState<string>('')
    const required = {value: true, message: t('this_field_is_required')}

    // When another implant was placed on the same site, the product cannot still be in the
    // patient's mouth. The radio's options are inverted (value 'YES' == "No, not in mouth"),
    // so we force 'YES', lock the radio and the removal date becomes mandatory.
    const anotherImplantPlaced =
        complaint.generalComplaint.wasAnotherImplantPlaced === 'YES'

    const {
        data,
        errors,
        handleChangeEvent,
        handleChange,
        validateFields,
        enableOnChangeValidation,
    } = useForm<TreatmentDatesState>({
        initialValues: {
            ...complaint.treatmentDates,
            wasImplantLoaded:
                complaint.treatmentDates.wasImplantLoaded || (complaint.generalComplaint.productType === 'ABUTMENT'
                    ? 'NO'
                    : complaint.treatmentDates.wasImplantLoaded),
            wasThereASecondSurgery:
                complaint.treatmentDates.wasThereASecondSurgery,
            wasProductRemoved: anotherImplantPlaced
                ? 'YES'
                : complaint.treatmentDates.wasProductRemoved,
        },
        validations: {
            dateOfTreatment: {
                required,
                custom: {
                    isValid: isValidDate,
                    message: t('please_enter_a_valid_date', {
                        earliestDate: t('earliest_date'),
                    }),
                },
            },
            wasImplantLoaded: {required},
            dateOfImplantLoading: {
                required: {
                    value: (value, data) => {
                        if (complaint.generalComplaint.productType === 'ABUTMENT') {
                            return true
                        } else {
                            return !(
                                data.wasImplantLoaded === 'YES' &&
                                !data.dateOfImplantLoading
                            )
                        }
                    },
                    message: t('this_field_is_required'),
                },
                custom: {
                    isValid: (value, data) => {
                        // If the implant was not loaded then we don't need to
                        // validate the date field
                        if (data.wasImplantLoaded !== 'YES') {
                            return true
                        } else {
                            // If it was loaded, then validate the date field
                            return isValidDate(value, data)
                        }
                    },
                    message: t('please_enter_a_valid_date', {
                        earliestDate: t('earliest_date'),
                    }),
                },
            },
            wasThereASecondSurgery: {required},
            dateOfSecondSurgery: {
                required: {
                    value: (value, data) => {
                        if (complaint.generalComplaint.productType === 'ABUTMENT') {
                            return true
                        } else {
                            return !(
                                data.wasThereASecondSurgery === 'YES' &&
                                !data.dateOfSecondSurgery
                            )
                        }
                    },
                    message: t('this_field_is_required'),
                },
                custom: {
                    isValid: (value, data) => {
                        if (data.wasThereASecondSurgery !== 'YES') {
                            return true
                        } else {
                            return isValidDate(value, data)
                        }
                    },
                    message: t('please_enter_a_valid_date', {
                        earliestDate: t('earliest_date'),
                    }),
                },
            },
            wasProductRemoved: {
                required
            },
            dateProductWasRemoved: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.wasProductRemoved === 'YES' && !data.dateProductWasRemoved
                        )
                    },
                    message: t('this_field_is_required'),
                },
                custom: {
                    isValid: (value, data) => {
                        if (data.wasProductRemoved !== 'YES') {
                            return true
                        } else {
                            return isValidDate(value, data)
                        }
                    },
                    message: t('please_enter_a_valid_date', {
                        earliestDate: t('earliest_date'),
                    }),
                },
            },
        },
        changeWatchers: {
            dateOfTreatment: {
                setValue: (prevState, currentState, newState) => {
                    let modifiedState: TreatmentDatesState = {
                        ...newState,
                    }

                    if (
                        prevState.wasImplantLoaded === 'YES' &&
                        newState.wasImplantLoaded !== 'YES'
                    ) {
                        modifiedState = {
                            ...modifiedState,
                            dateOfImplantLoading: null,
                        }
                    }

                    if (
                        prevState.wasThereASecondSurgery === 'YES' &&
                        newState.wasThereASecondSurgery !== 'YES'
                    ) {
                        modifiedState = {
                            ...modifiedState,
                            dateOfSecondSurgery: null,
                        }
                    }

                    if (
                        prevState.wasProductRemoved === 'YES' &&
                        newState.wasProductRemoved !== 'YES'
                    ) {
                        modifiedState = {
                            ...modifiedState,
                            dateProductWasRemoved: null,
                        }
                    }

                    return modifiedState
                },
            },
        },
    })

    const navigateToNextScreen = () => {
        navigateForward()
    }

    // Date of Placement validations
    useEffect(() => {

        const isPlacementOnOrBeforeDateOfOccurrence =

            !data.dateOfTreatment
            || !isValidDate(data.dateOfTreatment as Date, data)
            || isBefore(
                endOfDay(data.dateOfTreatment as Date) as Date,
                complaint.complaintType.dateOfOccurrence as Date
            )
            || isSameDay(
                endOfDay(data.dateOfTreatment as Date) as Date,
                complaint.complaintType.dateOfOccurrence as Date
            )

        const isPlacementBeforeSecondSurgery =
            !data.dateOfTreatment
            || !isValidDate(data.dateOfTreatment as Date, data)
            || !data.dateOfSecondSurgery
            || !isValidDate(data.dateOfSecondSurgery as Date, data)
            || isBefore(
                endOfDay(data.dateOfTreatment as Date) as Date,
                endOfDay(data.dateOfSecondSurgery as Date) as Date
            )

        const isPlacementOnOrBeforeRemoval =
            !data.dateOfTreatment
            || !isValidDate(data.dateOfTreatment as Date, data)
            || !data.dateProductWasRemoved
            || !isValidDate(data.dateProductWasRemoved as Date, data)
            || (isBefore(
                    endOfDay(data.dateOfTreatment as Date) as Date,
                    endOfDay(data.dateProductWasRemoved as Date) as Date
                )
                || isSameDay(
                    endOfDay(data.dateOfTreatment as Date) as Date,
                    endOfDay(data.dateProductWasRemoved as Date) as Date
                ))

        const isPlacementOnOrBeforeLoading =
            !data.dateOfTreatment
            || !isValidDate(data.dateOfTreatment as Date, data)
            || !data.dateOfImplantLoading
            || !isValidDate(data.dateOfImplantLoading as Date, data)
            || (isBefore(
                    endOfDay(data.dateOfTreatment as Date) as Date,
                    endOfDay(data.dateOfImplantLoading as Date) as Date
                )
                || isSameDay(
                    endOfDay(data.dateOfTreatment as Date) as Date,
                    endOfDay(data.dateOfImplantLoading as Date) as Date
                ))


        if (!isPlacementOnOrBeforeDateOfOccurrence) {
            setDateOfPlacementErrorMsg(
                t('td_date_of_placement_validation_message_4')
            )
        } else if (!isPlacementOnOrBeforeRemoval) {
            setDateOfPlacementErrorMsg(
                t('td_date_of_placement_validation_message_3')
            )
        } else if (!isPlacementBeforeSecondSurgery) {
            setDateOfPlacementErrorMsg(
                t('td_date_of_placement_validation_message_1')
            )
        } else if (!isPlacementOnOrBeforeLoading) {
            setDateOfPlacementErrorMsg(
                t('td_date_of_placement_validation_message_2')
            )
        } else {
            setDateOfPlacementErrorMsg('')
        }
    }, [
        data.dateOfTreatment,
        complaint.complaintType.dateOfOccurrence,
        data.dateOfSecondSurgery,
        data.dateOfImplantLoading,
        data.dateProductWasRemoved,
        setDateOfPlacementErrorMsg,
    ])

    // Date of Implant Loading validations
    useEffect(() => {

        const isImplantLoadingAfterSecondSurgery =
            !data.dateOfSecondSurgery
            || !isValidDate(data.dateOfSecondSurgery as Date, data)
            || !data.dateOfImplantLoading
            || !isValidDate(data.dateOfImplantLoading as Date, data)
            || complaint.generalComplaint.productType as ProductTypeOptions === 'ABUTMENT'
            || isAfter(
                endOfDay(data.dateOfImplantLoading as Date) as Date,
                endOfDay(data.dateOfSecondSurgery as Date) as Date
            )

        const isImplantLoadingOnOrBeforeEventOccurrence =
            !data.dateOfImplantLoading
            || !isValidDate(data.dateOfImplantLoading as Date, data)
            || isBefore(
                endOfDay(data.dateOfImplantLoading as Date) as Date,
                complaint.complaintType.dateOfOccurrence as Date
            )
            || isSameDay(
                endOfDay(data.dateOfImplantLoading as Date) as Date,
                endOfDay(
                    complaint.complaintType.dateOfOccurrence as Date
                ) as Date
            )

        const isImplantLoadingSameOrBeforeRemovalDate =
            !data.dateOfImplantLoading
            || !isValidDate(data.dateOfImplantLoading as Date, data)
            || !data.dateProductWasRemoved
            || !isValidDate(data.dateProductWasRemoved as Date, data)
            || isBefore(
                endOfDay(data.dateOfImplantLoading as Date) as Date,
                endOfDay(data.dateProductWasRemoved as Date) as Date
            )
            || isSameDay(
                endOfDay(data.dateOfImplantLoading as Date) as Date,
                endOfDay(data.dateProductWasRemoved as Date) as Date
            )

        const isImplantLoadingOnOrAfterTreatmentDate =
            !data.dateOfImplantLoading
            || !isValidDate(data.dateOfImplantLoading as Date, data)
            || !data.dateOfTreatment
            || !isValidDate(data.dateOfTreatment as Date, data)
            || isAfter(
                endOfDay(data.dateOfImplantLoading as Date) as Date,
                endOfDay(data.dateOfTreatment as Date) as Date
            )
            || isSameDay(
                endOfDay(data.dateOfImplantLoading as Date) as Date,
                endOfDay(data.dateOfTreatment as Date) as Date
            )

        if (!isImplantLoadingAfterSecondSurgery) {
            setDateOfImplantLoadingErrorMsg(
                t('td_date_of_implant_loading_validation_message_3')
            )
        } else if (!isImplantLoadingOnOrBeforeEventOccurrence) {
            setDateOfImplantLoadingErrorMsg(
                t('td_date_of_implant_loading_validation_message_2')
            )
        } else if (!isImplantLoadingSameOrBeforeRemovalDate) {
            setDateOfImplantLoadingErrorMsg(
                t('td_date_of_implant_loading_validation_message_4')
            )
        } else if (!isImplantLoadingOnOrAfterTreatmentDate) {
            setDateOfImplantLoadingErrorMsg(
                t('td_date_of_implant_loading_validation_message_1')
            )
        } else {
            setDateOfImplantLoadingErrorMsg('')
        }
    }, [
        data.dateOfImplantLoading,
        data.dateOfSecondSurgery,
        data.dateProductWasRemoved,
        data.dateOfTreatment,
        complaint.complaintType.dateOfOccurrence,
        setDateOfImplantLoadingErrorMsg,
    ])

    // Date of Second Surgery validations
    useEffect(() => {


        const isSecondSurgeryBeforeOrOnDateOfOccurrence =
            !data.dateOfSecondSurgery
            || !isValidDate(data.dateOfSecondSurgery as Date, data)
            || isBefore(
                endOfDay(data.dateOfSecondSurgery as Date) as Date,
                complaint.complaintType.dateOfOccurrence as Date
            )
            || isSameDay(
                endOfDay(data.dateOfSecondSurgery as Date) as Date,
                complaint.complaintType.dateOfOccurrence as Date
            )

        const isSecondSurgeryBeforeLoadingDate =
            !data.dateOfImplantLoading
            || !isValidDate(data.dateOfImplantLoading as Date, data)
            || !data.dateOfSecondSurgery
            || !isValidDate(data.dateOfSecondSurgery as Date, data)
            || isBefore(
                endOfDay(data.dateOfSecondSurgery as Date) as Date,
                endOfDay(data.dateOfImplantLoading as Date) as Date
            )

        const isSecondSurgeryBeforeOrSameDayAsRemovalDate =
            !data.dateProductWasRemoved
            || !isValidDate(data.dateProductWasRemoved as Date, data)
            || !data.dateOfSecondSurgery
            || !isValidDate(data.dateOfSecondSurgery as Date, data)
            || isBefore(
                endOfDay(data.dateOfSecondSurgery as Date) as Date,
                endOfDay(data.dateProductWasRemoved as Date) as Date
            )
            || isSameDay(
                endOfDay(data.dateOfSecondSurgery as Date) as Date,
                endOfDay(data.dateProductWasRemoved as Date) as Date
            )

        const isSecondSurgeryAfterTreatmentDate =
            !data.dateOfTreatment
            || !isValidDate(data.dateOfTreatment as Date, data)
            || !data.dateOfSecondSurgery
            || !isValidDate(data.dateOfSecondSurgery as Date, data)
            || isAfter(
                endOfDay(data.dateOfSecondSurgery as Date) as Date,
                endOfDay(data.dateOfTreatment as Date) as Date
            )

        if (!isSecondSurgeryAfterTreatmentDate) {
            setDateOfSecondSurgeryErrorMsg(
                t('td_date_of_second_surgery_validation_message')
            )
        } else if (!isSecondSurgeryBeforeLoadingDate) {
            setDateOfSecondSurgeryErrorMsg(
                t('td_date_of_second_surgery_validation_message2')
            )
        } else if (!isSecondSurgeryBeforeOrSameDayAsRemovalDate) {
            setDateOfSecondSurgeryErrorMsg(
                t('td_date_of_second_surgery_validation_message3')
            )
        } else if (!isSecondSurgeryBeforeOrOnDateOfOccurrence) {
            setDateOfSecondSurgeryErrorMsg(
                t('td_date_of_second_surgery_validation_message4')
            )
        } else {
            setDateOfSecondSurgeryErrorMsg('')
        }
    }, [
        data.dateOfSecondSurgery,
        complaint.complaintType.dateOfOccurrence,
        data.dateOfImplantLoading,
        data.dateProductWasRemoved,
        data.dateOfTreatment,
        setDateOfSecondSurgeryErrorMsg,
    ])

    // Date of Removal validations
    useEffect(() => {

        const isRemovalDateAfterSecondSurgery =
            !data.dateProductWasRemoved
            || !isValidDate(data.dateProductWasRemoved as Date, data)
            || !data.dateOfSecondSurgery
            || !isValidDate(data.dateOfSecondSurgery as Date, data)
            || isAfter(
                endOfDay(data.dateProductWasRemoved as Date) as Date,
                endOfDay(data.dateOfSecondSurgery as Date) as Date
            )
            || isSameDay(
                endOfDay(data.dateProductWasRemoved as Date) as Date,
                endOfDay(data.dateOfSecondSurgery as Date) as Date
            )

        const isProductRemovalOnOrAfterTreatmentDate =
            !data.dateProductWasRemoved
            || !isValidDate(data.dateProductWasRemoved as Date, data)
            || !data.dateOfTreatment
            || !isValidDate(data.dateOfTreatment as Date, data)
            || isAfter(
                endOfDay(data.dateProductWasRemoved as Date) as Date,
                endOfDay(data.dateOfTreatment as Date) as Date
            )
            || isSameDay(
                endOfDay(data.dateProductWasRemoved as Date) as Date,
                endOfDay(data.dateOfTreatment as Date) as Date
            )

        const isProductRemovalOnOrAfterImplantLoading =
            !data.dateOfImplantLoading
            || !isValidDate(data.dateOfImplantLoading as Date, data)
            || !data.dateProductWasRemoved
            || !isValidDate(data.dateProductWasRemoved as Date, data)
            || isAfter(
                endOfDay(data.dateProductWasRemoved as Date) as Date,
                endOfDay(data.dateOfImplantLoading as Date) as Date
            )
            || isSameDay(
                endOfDay(data.dateProductWasRemoved as Date) as Date,
                endOfDay(data.dateOfImplantLoading as Date) as Date
            )
        const isProductRemovalOnOrAfterDateOfOccurence =
            !data.dateProductWasRemoved
            || !isValidDate(data.dateProductWasRemoved as Date, data)
            || isAfter(
                endOfDay(data.dateProductWasRemoved as Date) as Date,
                endOfDay(complaint.complaintType.dateOfOccurrence as Date) as Date
            )
            || isSameDay(
                endOfDay(data.dateProductWasRemoved as Date) as Date,
                endOfDay(complaint.complaintType.dateOfOccurrence as Date) as Date
            )

        if (!isProductRemovalOnOrAfterDateOfOccurence) {
            setDateOfProductRemovalErrorMsg(
                t('td_date_product_was_removed_validation_message2')
            )
        } else if (!isProductRemovalOnOrAfterImplantLoading) {
            setDateOfProductRemovalErrorMsg(
                t('td_date_product_was_removed_validation_message_after_loading')
            )
        } else if (!isProductRemovalOnOrAfterTreatmentDate) {
            setDateOfProductRemovalErrorMsg(
                t('td_date_product_was_removed_validation_message')
            )
        } else if (!isRemovalDateAfterSecondSurgery) {
            setDateOfProductRemovalErrorMsg(
                t('td_date_product_was_removed_validation_message_after_second_surgery')
            )
        } else {
            setDateOfProductRemovalErrorMsg('')
        }
    }, [
        data.dateProductWasRemoved,
        data.dateOfSecondSurgery,
        data.dateOfTreatment,
        data.dateProductWasRemoved,
        complaint.complaintType.dateOfOccurrence,
        setDateOfProductRemovalToSecondarySurgeryErrorMsg,
    ])

    const handleSubmit = () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid =
            validateFields(data) &&
            !dateOfImplantLoadingErrorMsg &&
            !dateOfSecondSurgeryErrorMsg &&
            !dateOfProductRemovalErrorMsg &&
            !dateOfProductRemovalToSecondarySurgeryErrorMsg

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // Package up the form data and update app state
        const treatmentDates: TreatmentDatesState = {
            ...data,
            wasThereASecondSurgery: data.wasThereASecondSurgery,
            dateOfSecondSurgery: data.dateOfSecondSurgery,
            wasProductRemoved: data.wasProductRemoved,
            dateProductWasRemoved: data.dateProductWasRemoved,
        }

        dispatch(setTreatmentDates(treatmentDates))

        navigateToNextScreen()
    }

    return (
        <Grid container>
            <Stack spacing={2}>
                <Typography variant='h5' sx={{mb: 2}}>
                    {t('td_treatment_dates')}
                </Typography>

                <Alert severity='info'>{t('unknown_date_instructions')}</Alert>

                {/* Date of Treatment */}
                <FormControl
                    sx={{maxWidth: '350px'}}
                    // error={!!errors.dateOfTreatment}
                >
                    <FormLabel sx={{mb: 1}}>
                        {t('td_date_of_treatment')}:
                    </FormLabel>
                    <DatePicker
                        maxDate={complaint.complaintType.dateOfOccurrence}
                        disableFuture
                        mask={dateMask}
                        value={data.dateOfTreatment}
                        onChange={(date) => {
                            handleChange(
                                'dateOfTreatment',
                                date as unknown as Date
                            )
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                // error={!!errors.dateOfTreatment}
                            />
                        )}
                    />
                    <FormHelperText error={!!errors.dateOfTreatment}>
                        {errors.dateOfTreatment}
                    </FormHelperText>
                    <FormHelperText
                        error={!!dateOfPlacementErrorMsg}
                    >
                        {dateOfPlacementErrorMsg}
                    </FormHelperText>
                </FormControl>

                {/* Was There a Second Surgery */}
                {
                    <Stack spacing={0}>
                        <FormControl sx={{pt: 1}}>
                            <FormLabel>
                                {t('td_was_there_a_second_surgery')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={data.wasThereASecondSurgery}
                                onChange={handleChangeEvent(
                                    'wasThereASecondSurgery'
                                )}
                            >
                                <FormControlLabel
                                    value='YES'
                                    control={<Radio/>}
                                    label={t('option_yes') as string}
                                />
                                <FormControlLabel
                                    value='NO'
                                    control={<Radio/>}
                                    label={t('option_no') as string}
                                />
                            </RadioGroup>
                            <FormHelperText
                                error={!!errors.wasThereASecondSurgery}
                            >
                                {errors.wasThereASecondSurgery}
                            </FormHelperText>
                        </FormControl>

                        <FormControl sx={{maxWidth: '350px'}}>
                            <FormLabel sx={{mb: 1}}>
                                {t('td_date_of_second_surgery')}:
                            </FormLabel>
                            <DatePicker
                                disableFuture
                                disabled={
                                    data.wasThereASecondSurgery !== 'YES'
                                }
                                mask={dateMask}
                                value={data.dateOfSecondSurgery}
                                onChange={(date) => {
                                    handleChange(
                                        'dateOfSecondSurgery',
                                        date as unknown as Date
                                    )
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />
                            <FormHelperText
                                error={!!errors.dateOfSecondSurgery}
                            >
                                {errors.dateOfSecondSurgery}
                            </FormHelperText>

                            <FormHelperText
                                error={!!dateOfSecondSurgeryErrorMsg}
                            >
                                {dateOfSecondSurgeryErrorMsg}
                            </FormHelperText>
                        </FormControl>
                    </Stack>}

                {/* Was Implant Loaded */}
                {(complaint.generalComplaint.productType !== 'ABUTMENT' || complaint.complaintType.location === '5_CLINIC_AFTER_PROCEDURE') && (
                    <Stack spacing={0}>
                        <FormControl sx={{pt: 1}}>
                            <FormLabel>{t('td_was_implant_loaded')}</FormLabel>
                            <RadioGroup
                                row
                                value={data.wasImplantLoaded}
                                onChange={handleChangeEvent('wasImplantLoaded')}
                            >
                                <FormControlLabel
                                    value='YES'
                                    control={<Radio/>}
                                    label={t('option_yes') as string}
                                />
                                <FormControlLabel
                                    value='NO'
                                    control={<Radio/>}
                                    label={t('option_no') as string}
                                />
                            </RadioGroup>
                            <FormHelperText error={!!errors.wasImplantLoaded}>
                                {errors.wasImplantLoaded}
                            </FormHelperText>
                        </FormControl>

                        <FormControl sx={{maxWidth: '350px'}}>
                            <FormLabel sx={{mb: 1}}>
                                {t('td_date_of_implant_loading')}:
                            </FormLabel>
                            <DatePicker
                                disableFuture
                                disabled={data.wasImplantLoaded !== 'YES'}
                                mask={dateMask}
                                value={data.dateOfImplantLoading}
                                onChange={(date) => {
                                    handleChange(
                                        'dateOfImplantLoading',
                                        date as unknown as Date
                                    )
                                }}
                                maxDate={data.dateProductWasRemoved}
                                minDate={data.dateOfSecondSurgery}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />
                            <FormHelperText
                                error={!!errors.dateOfImplantLoading}
                            >
                                {errors.dateOfImplantLoading}
                            </FormHelperText>

                            <FormHelperText
                                error={!!dateOfImplantLoadingErrorMsg}
                            >
                                {dateOfImplantLoadingErrorMsg}
                            </FormHelperText>
                        </FormControl>
                    </Stack>
                )}

                {/* Was the Product Removed */}
                {
                    <Stack spacing={0}>
                        <FormControl sx={{pt: 1}}>
                            <FormLabel>
                                {t('td_was_the_product_removed')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={data.wasProductRemoved}
                                onChange={handleChangeEvent('wasProductRemoved')}
                            >
                                {/* MUI's RadioGroup does not forward `disabled`,
                                    so it has to be set on each option. */}
                                <FormControlLabel
                                    value='NO'
                                    control={<Radio/>}
                                    disabled={anotherImplantPlaced}
                                    label={t('option_yes') as string}
                                />
                                <FormControlLabel
                                    value='YES'
                                    control={<Radio/>}
                                    disabled={anotherImplantPlaced}
                                    label={t('option_no') as string}
                                />
                            </RadioGroup>
                            <FormHelperText error={!!errors.wasProductRemoved}>
                                {errors.wasProductRemoved}
                            </FormHelperText>
                        </FormControl>

                        <FormControl sx={{maxWidth: '350px'}}>
                            <FormLabel sx={{mb: 1}}>
                                {t('td_date_product_was_removed')}:
                            </FormLabel>
                            <DatePicker
                                minDate={complaint.complaintType.dateOfOccurrence}
                                disabled={data.wasProductRemoved !== 'YES'}
                                disableFuture
                                mask={dateMask}
                                value={data.dateProductWasRemoved}
                                onChange={(date) => {
                                    handleChange(
                                        'dateProductWasRemoved',
                                        date as unknown as Date
                                    )
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />
                            <FormHelperText
                                error={!!errors.dateProductWasRemoved}
                            >
                                {errors.dateProductWasRemoved}
                            </FormHelperText>

                            <FormHelperText
                                error={!!dateOfProductRemovalErrorMsg}
                            >
                                {dateOfProductRemovalErrorMsg}
                            </FormHelperText>
                            <FormHelperText
                                error={!!dateOfProductRemovalToSecondarySurgeryErrorMsg}
                            >
                                {dateOfProductRemovalToSecondarySurgeryErrorMsg}
                            </FormHelperText>
                        </FormControl>
                    </Stack>}
            </Stack>

            <Stack
                direction='row-reverse'
                spacing={2}
                sx={{pt: 4, width: '100%'}}
            >
                <Button
                    variant='contained'
                    color='primary'
                    onClick={handleSubmit}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => dispatch(setTreatmentDates(data))}/>

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default TreatmentDates
